import React, { useState, useCallback } from "react";
import parseISO from "date-fns/parseISO";
import { useForm, FormProvider } from "react-hook-form";
import isValid from "date-fns/isValid";
import * as R from "ramda";
import {
  StatusNotice,
  StatusNoticeProps,
  EventTypeEnum,
} from "../../components/StatusNotice/StatusNotice";
import { Field } from "../../components/Field/Field";
import { DrawingCodeWarning } from "../../components/DrawingCodeWarning/DrawingCodeWarning";
import { Button } from "../../components/Button/Button";
import {
  ViewerOrganizations,
  useUpdatePurchaseRequestProduct,
  useCreateQuantityUnit,
  useRemovePurchaseRequestItemAttachment,
  UpdatePurchaseRequestProductMutationVariables,
} from "../../schema";
import { FieldWrap } from "../../components/FieldWrap/FieldWrap";
import { Datepicker } from "../../components/Datepicker/Datepicker";
import { Select } from "../../components/Select/Select";
import { currencies } from "../../constants";
import { Modal } from "../../components/Modal/Modal";
import {
  ItemAutosuggest,
  AutosuggestItem,
} from "../../components/ItemAutosuggest/ItemAutosuggest";
import { matches } from "../../services/matches";
import {
  QuantityUnitInfo,
  quantityUnitToAutosuggestItem,
} from "../../services/quantityUnitToAutosuggestItem";
import { Accordion } from "../../components/Accordion/Accordion";
import { Attachments } from "../../components/Attachments/Attachments";
import { NonUploadedFiles } from "../../components/NonUploadedFiles/NonUploadedFiles";
import { FieldLabel } from "../../components/FieldLabel/FieldLabel";
import { UploadedAttachment } from "../../components/UploadedAttachment/UploadedAttachment";
import { NotAvailable } from "../../components/NotAvailable/NotAvailable";
import styles from "../PurchaseRequestView/PurchaseRequestView.module.scss";
import { OnDropCallbackFn } from "../../components/Dropzone/Dropzone";
import { NumberInput } from "../../components/NumberInput/NumberInput";
import { CustomFieldList } from "../../components/CustomFieldList/CustomFieldList";
import { isDefined } from "../../services/isDefined";
import { EsourcingFieldList } from "../../components/EsourcingFieldList/EsourcingFieldList";
import { ItemParams } from "./EditPurchaseRequestItemView";

export interface RequestProductFormProps {
  organization: ViewerOrganizations;
  product: ItemParams;
  purchaseRequestCode?: string;
  onSuccess(): void;
  onCancel(): void;
  onModalClose(): void;
}

export interface UpdatePurchaseRequestProductFormInputs
  extends Omit<UpdatePurchaseRequestProductMutationVariables, "customFields"> {
  customFields: { label: string; value: string | number | null }[];
  esourcingFields: string | null;
}

export const EditPurchaseRequestProduct: React.FC<RequestProductFormProps> = ({
  organization,
  product,
  onSuccess,
  onCancel,
  onModalClose,
}) => {
  // state change status notice state
  const [statusNotice, setStatusNotice] =
    React.useState<StatusNoticeProps | null>(null);
  const statusNoticeRef = React.useRef<HTMLDivElement>(null);

  // unit related state
  const units = organization.quantityUnits as QuantityUnitInfo[];

  const [chosenQuantityUnit, setChosenQuantityUnit] =
    useState<AutosuggestItem>();
  const [quantityUnitDisplayValue, setQuantityUnitDisplayValue] = useState("");

  React.useEffect(() => {
    if (units && product.unit) {
      setChosenQuantityUnit(units.find((unit) => unit.name === product.unit));
      setQuantityUnitDisplayValue(product.unit);
    }
  }, [product, units]);

  const [responseDeadline, setResponseDeadline] = React.useState<Date>();

  // set local state from pre-existing form values if present and valid
  React.useEffect(() => {
    if (product.expectedDeliveryDate) {
      const parsedDateTime = parseISO(product.expectedDeliveryDate);

      isValid(parsedDateTime) && setResponseDeadline(parsedDateTime);
    }
  }, [product]);

  const [files, setFiles] = useState<File[]>();

  type customFieldType = typeof product.customFields;

  const defaultValues: UpdatePurchaseRequestProductFormInputs = {
    purchaseRequestItemId: product.id,
    name: product.name,
    code: product.code ?? null,
    quantity: product.quantity ?? 0,
    unit: product.unit ?? null,
    currency:
      isDefined(product.currency) &&
      product.currency !== null &&
      R.not(R.isEmpty(product.currency))
        ? product.currency
        : organization.baseCurrency,
    unitPrice: product.unitPrice ?? null,
    suggestedSupplier: product.suggestedSupplier ?? null,
    supplierCode: product.supplierCode ?? null,
    expectedDeliveryDate: product.expectedDeliveryDate
      ? parseISO(product.expectedDeliveryDate)
      : null,
    additionalInfo: product.additionalInfo ?? null,
    productUrl: product.productUrl ?? null,
    customFields:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- load-bearing: a concrete type breaks the build or changes behaviour; revisit during refactor/stack upgrade
      R.compose<customFieldType, NonNullable<customFieldType>, any, any>(
        R.map(R.zipObj(["label", "value"])),
        R.toPairs,
        R.defaultTo({}),
      )(product.customFields) ?? [],
    attachments: null,
    esourcingFields: product.esourcingFields ?? null,
  };

  const formMethods = useForm<UpdatePurchaseRequestProductFormInputs>({
    defaultValues,
  });

  const { register, handleSubmit } = formMethods;

  // mutation to update the purchase request product
  const [
    updatePurchaseRequestProduct,
    { loading: isUpdatePurchaseRequestProductLoading },
  ] = useUpdatePurchaseRequestProduct({
    refetchQueries: [
      "PaginatedPurchaseRequests",
      "PurchaseRequestByCode",
      "GetItemById",
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setStatusNotice({
        level: EventTypeEnum.SUCCESS,
        eventTitle: "Successfully saved!",
      });

      // scroll to status message
      statusNoticeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    onError: () => {
      setStatusNotice({
        level: EventTypeEnum.ALERT,
        eventTitle: "Sorry! There was an error when saving the product",
        eventDescription: "Please try again",
      });

      // scroll to status message
      statusNoticeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
  });

  // setup creating unit
  const [createQuantityUnit, { loading: isCreatingQuantityUnit }] =
    useCreateQuantityUnit({
      refetchQueries: ["Viewer"],
      awaitRefetchQueries: true,
    });

  // setup removing attachment
  const [removeAttachment] = useRemovePurchaseRequestItemAttachment({
    refetchQueries: ["PurchaseRequestByCode", "GetItemById"],
    awaitRefetchQueries: true,
  });

  // set files state on drop
  const onDrop = useCallback<OnDropCallbackFn>(
    (acceptedFiles) => {
      // set selected file
      setFiles([...(files ?? []), ...acceptedFiles]);
    },
    [files],
  );

  // handle saving the form
  const onSave = async (
    formData: UpdatePurchaseRequestProductMutationVariables,
  ) => {
    const customFieldsToMutationFormat = R.compose<
      { label: string; value: string }[],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- load-bearing: a concrete type breaks the build or changes behaviour; revisit during refactor/stack upgrade
      any[],
      { [k: string]: string },
      string
    >(
      JSON.stringify,
      R.fromPairs,
      R.map(R.values),
    )(
      (formData.customFields as unknown as {
        label: string;
        value: string;
      }[]) ?? [],
    );

    // call the mutation with formData
    await updatePurchaseRequestProduct({
      variables: {
        ...defaultValues,
        ...formData,
        expectedDeliveryDate: responseDeadline ?? null,
        quantity: parseFloat(formData.quantity.toString()),
        unit: chosenQuantityUnit?.name ? chosenQuantityUnit.name : null,
        unitPrice: formData.unitPrice
          ? parseFloat(formData.unitPrice.toString())
          : null,
        currency: R.not(R.isEmpty(formData.currency))
          ? formData.currency
          : organization.baseCurrency,
        attachments: files ?? [],
        customFields: customFieldsToMutationFormat,
        esourcingFields: JSON.stringify(formData.esourcingFields) ?? null,
      },
    });

    // empty files list
    setFiles([]);

    onSuccess();
  };

  const filterQuantityUnits = async (value: string) =>
    value.length === 0
      ? units
      : units
          .filter((unit) => matches(value, unit.name))
          .map(quantityUnitToAutosuggestItem);

  return (
    <Modal
      title={product.name}
      onCloseRequested={() => onModalClose()}
      footer={
        <>
          <Button data-testid="0e9ac26ab2" secondary onClick={onCancel}>
            Cancel
          </Button>

          <Button
            form="purchase-request-product-form"
            type="submit"
            loading={isUpdatePurchaseRequestProductLoading}
            disabled={isUpdatePurchaseRequestProductLoading}
            data-testid="87b4377935"
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles["wrap"]}>
        {/* status notice */}
        {statusNotice && (
          <div ref={statusNoticeRef}>
            <StatusNotice
              level={statusNotice.level}
              eventTitle={statusNotice.eventTitle}
              eventDescription={statusNotice.eventDescription}
              onCloseRequested={() => {
                setStatusNotice(null);
              }}
            />
          </div>
        )}

        <form
          id="purchase-request-product-form"
          onSubmit={handleSubmit(onSave)}
        >
          <FormProvider {...formMethods}>
            <Field
              name={"name"}
              inputRef={register({ required: "Name is required" })}
              short
              required
              autofocus
              label="Product name"
              placeholder="Item name"
            />

            <FieldWrap required label="SPECIFY THE QUANTITY">
              <NumberInput
                name={"quantity"}
                inputRef={register({ required: "Quantity is required" })}
                placeholder="Quantity"
              />
              <ItemAutosuggest
                required
                placeholder="Unit"
                fetchSuggestions={filterQuantityUnits}
                value={quantityUnitDisplayValue}
                chosenSuggestion={chosenQuantityUnit}
                onChange={(newValue) => setQuantityUnitDisplayValue(newValue)}
                onChoose={(unit) => setChosenQuantityUnit(unit)}
                onCreateNew={async (name) => {
                  setChosenQuantityUnit(undefined);
                  setQuantityUnitDisplayValue("");

                  const createdQuantityUnitResponse = await createQuantityUnit({
                    variables: { organizationId: organization.id, name: name },
                  });

                  const createdUnit = createdQuantityUnitResponse.data
                    ?.createQuantityUnit as QuantityUnitInfo;
                  setChosenQuantityUnit(
                    quantityUnitToAutosuggestItem(createdUnit),
                  );
                  setQuantityUnitDisplayValue(createdUnit.name);
                }}
                loading={isCreatingQuantityUnit}
              />
            </FieldWrap>

            <Accordion isInitiallyOpen={false} title="Optional">
              <Field
                name={"suggestedSupplier"}
                inputRef={register}
                short
                label="Suggested Supplier"
              />
              <NumberInput
                name={"unitPrice"}
                inputRef={register}
                short
                label="Unit price"
                addon={
                  <Select
                    name={"currency"}
                    options={currencies.map((currency) => ({
                      value: currency.symbol,
                      label: currency.symbol,
                    }))}
                    inputRef={register}
                    secondary
                  />
                }
              />
              <Datepicker
                onChange={(date) => {
                  if (date) {
                    setResponseDeadline(date);
                  }
                }}
                label="When do you want it delivered"
                minDate={new Date()}
                selected={responseDeadline}
                onClear={() => {
                  setResponseDeadline(undefined);
                }}
              />
              <Field
                name={"productUrl"}
                inputRef={register}
                short
                label="Link"
              />
              <Field
                name={"additionalInfo"}
                textareaRef={register}
                textarea
                short
                label="Additional info"
              />
              <Field
                name={"code"}
                inputRef={register}
                short
                label="Item code"
              />
              <DrawingCodeWarning
                enabled={!!organization.drawingsEnabled}
                control={formMethods.control}
                nameField="name"
                codeField="code"
                defaultName={product.name}
                defaultCode={product.code}
              />
              <Field
                name={"supplierCode"}
                inputRef={register}
                short
                label="Supplier code"
              />
              {/* Custom Fields */}
              <FieldLabel label="Custom Fields" />
              <CustomFieldList className={styles["custom-fields-list"]} />

              <FieldLabel label="Attachments" />
              <div className={styles["file-list"]}>
                {product.attachments && product.attachments.length > 0
                  ? product.attachments.map((attachment) => (
                      <UploadedAttachment
                        key={attachment.id}
                        attachment={attachment}
                        onRemove={() =>
                          removeAttachment({
                            variables: {
                              attachmentId: attachment.id,
                              purchaseRequestItemId: product.id,
                            },
                          })
                        }
                      />
                    ))
                  : !files && <NotAvailable alignLeft />}

                {files && files.length > 0 && (
                  <NonUploadedFiles files={files} setFiles={setFiles} />
                )}
              </div>
              <Attachments
                multiple
                loading={false}
                onDrop={(acceptedFiles) => onDrop(acceptedFiles)}
              />

              {/* Esourcing Fields */}
              {product.esourcingFields ? (
                <EsourcingFieldList />
              ) : (
                <>
                  <FieldLabel label="Esourcing Fields" />
                  <NotAvailable alignLeft />
                </>
              )}
            </Accordion>
          </FormProvider>
        </form>
      </div>
    </Modal>
  );
};
